/**
 * Zustand store for guided workflow management
 */

import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import {
  WorkflowState,
  WorkflowStepId,
  StepStatus,
  TemplateSelectionStatus,
  PlanningStatus,
  SlideGenerationStatus,
  AudioGenerationStatus,
  VideoGenerationStatus,
  WorkflowEvent,
} from '@/types/workflow';

interface WorkflowStoreState {
  // State - Map of conversationId to workflow state
  workflows: Map<string, WorkflowState>;

  // Actions
  initWorkflow: (conversationId: string, collectionType?: string) => void;
  setWorkflowVisibility: (conversationId: string, visible: boolean) => void;
  setWorkflowEnabled: (conversationId: string, enabled: boolean) => void;
  updateStepStatus: (
    conversationId: string,
    step: WorkflowStepId,
    updates: Partial<StepStatus>
  ) => void;
  updateTemplateSelectionStatus: (
    conversationId: string,
    updates: Partial<TemplateSelectionStatus>
  ) => void;
  updatePlanningStatus: (
    conversationId: string,
    updates: Partial<PlanningStatus>
  ) => void;
  updateSlideGenerationStatus: (
    conversationId: string,
    updates: Partial<SlideGenerationStatus>
  ) => void;
  updateAudioGenerationStatus: (
    conversationId: string,
    updates: Partial<AudioGenerationStatus>
  ) => void;
  updateVideoGenerationStatus: (
    conversationId: string,
    updates: Partial<VideoGenerationStatus>
  ) => void;
  progressToNextStep: (conversationId: string) => void;
  handleWorkflowEvent: (conversationId: string, event: WorkflowEvent) => void;
  toggleStepExpanded: (conversationId: string, step: WorkflowStepId) => void;
  resetWorkflow: (conversationId: string) => void;
  clearWorkflow: (conversationId: string) => void;

  // Getters
  getWorkflowState: (conversationId: string) => WorkflowState | null;
  isWorkflowVisible: (conversationId: string) => boolean;
  isWorkflowEnabled: (conversationId: string) => boolean;
  getCurrentStep: (conversationId: string) => WorkflowStepId | null;
  getStepStatus: (conversationId: string, step: WorkflowStepId) => StepStatus | null;
}

// Default step statuses
const createDefaultTemplateSelectionStatus = (): TemplateSelectionStatus => ({
  status: 'pending',
  progress: 0,
  canProceed: true, // First step is always accessible
  isExpanded: false,
});

const createDefaultPlanningStatus = (): PlanningStatus => ({
  status: 'pending',
  progress: 0,
  canProceed: false, // Must select template first
  isExpanded: false,
  subTasks: [],
});

const createDefaultSlideGenerationStatus = (): SlideGenerationStatus => ({
  status: 'pending',
  progress: 0,
  canProceed: false,
  isExpanded: false,
  slides: [],
  totalSlides: 0,
  completedSlides: 0,
  subTasks: [],
});

const createDefaultAudioGenerationStatus = (): AudioGenerationStatus => ({
  status: 'pending',
  progress: 0,
  canProceed: false, // Must complete slides first
  isExpanded: false,
});

const createDefaultVideoGenerationStatus = (): VideoGenerationStatus => ({
  status: 'pending',
  progress: 0,
  canProceed: false, // Must generate audio first
  isExpanded: false,
  substeps: {
    mapTiming: { status: 'pending' },
    assembly: { status: 'pending' },
    setup: { status: 'pending' },
  },
});

// Create default workflow state
const createDefaultWorkflowState = (conversationId: string, collectionType: string = 'VIDEO'): WorkflowState => ({
  conversationId,
  isVisible: true, // Show by default (visible immediately)
  isEnabled: true, // Enabled by default (resource analysis done on hero page)
  currentStep: 'template_selection', // Start with template selection (Step 1)
  steps: {
    template_selection: createDefaultTemplateSelectionStatus(),
    planning: createDefaultPlanningStatus(),
    slide_generation: createDefaultSlideGenerationStatus(),
    audio_generation: createDefaultAudioGenerationStatus(),
    video_generation: createDefaultVideoGenerationStatus(),
  },
  createdAt: new Date(),
  updatedAt: new Date(),
  collectionType, // Store collection type for UI rendering
});

export const useWorkflowStore = create<WorkflowStoreState>()(
  devtools(
    persist(
      (set, get) => ({
        workflows: new Map(),

        // Initialize workflow for a conversation
        initWorkflow: (conversationId: string, collectionType: string = 'VIDEO') => {
          set((state) => {
            const workflows = new Map(state.workflows);
            if (!workflows.has(conversationId)) {
              workflows.set(conversationId, createDefaultWorkflowState(conversationId, collectionType));
            }
            return { workflows };
          }, false, 'initWorkflow');
        },

        // Set workflow visibility (toggle panel)
        setWorkflowVisibility: (conversationId: string, visible: boolean) => {
          set((state) => {
            const workflows = new Map(state.workflows);
            const workflow = workflows.get(conversationId);
            if (workflow) {
              workflows.set(conversationId, {
                ...workflow,
                isVisible: visible,
                updatedAt: new Date(),
              });
            }
            return { workflows };
          }, false, 'setWorkflowVisibility');
        },

        // Enable/disable workflow for conversation
        setWorkflowEnabled: (conversationId: string, enabled: boolean) => {
          set((state) => {
            const workflows = new Map(state.workflows);
            const workflow = workflows.get(conversationId);
            if (workflow) {
              workflows.set(conversationId, {
                ...workflow,
                isEnabled: enabled,
                updatedAt: new Date(),
              });
            }
            return { workflows };
          }, false, 'setWorkflowEnabled');
        },

        // Update generic step status
        updateStepStatus: (
          conversationId: string,
          step: WorkflowStepId,
          updates: Partial<StepStatus>
        ) => {
          set((state) => {
            const workflows = new Map(state.workflows);
            const workflow = workflows.get(conversationId);
            if (workflow) {
              workflows.set(conversationId, {
                ...workflow,
                steps: {
                  ...workflow.steps,
                  [step]: {
                    ...workflow.steps[step],
                    ...updates,
                  },
                },
                updatedAt: new Date(),
              });
            }
            return { workflows };
          }, false, 'updateStepStatus');
        },

        // Update template selection status
        updateTemplateSelectionStatus: (
          conversationId: string,
          updates: Partial<TemplateSelectionStatus>
        ) => {
          set((state) => {
            const workflows = new Map(state.workflows);
            const workflow = workflows.get(conversationId);
            if (workflow) {
              workflows.set(conversationId, {
                ...workflow,
                steps: {
                  ...workflow.steps,
                  template_selection: {
                    ...workflow.steps.template_selection,
                    ...updates,
                  },
                },
                updatedAt: new Date(),
              });
            }
            return { workflows };
          }, false, 'updateTemplateSelectionStatus');
        },

        // Update planning status
        updatePlanningStatus: (
          conversationId: string,
          updates: Partial<PlanningStatus>
        ) => {
          set((state) => {
            const workflows = new Map(state.workflows);
            const workflow = workflows.get(conversationId);
            if (workflow) {
              workflows.set(conversationId, {
                ...workflow,
                steps: {
                  ...workflow.steps,
                  planning: {
                    ...workflow.steps.planning,
                    ...updates,
                  },
                },
                updatedAt: new Date(),
              });
            }
            return { workflows };
          }, false, 'updatePlanningStatus');
        },

        // Update slide generation status
        updateSlideGenerationStatus: (
          conversationId: string,
          updates: Partial<SlideGenerationStatus>
        ) => {
          set((state) => {
            const workflows = new Map(state.workflows);
            const workflow = workflows.get(conversationId);
            if (workflow) {
              workflows.set(conversationId, {
                ...workflow,
                steps: {
                  ...workflow.steps,
                  slide_generation: {
                    ...workflow.steps.slide_generation,
                    ...updates,
                  },
                },
                updatedAt: new Date(),
              });
            }
            return { workflows };
          }, false, 'updateSlideGenerationStatus');
        },

        // Update audio generation status
        updateAudioGenerationStatus: (
          conversationId: string,
          updates: Partial<AudioGenerationStatus>
        ) => {
          set((state) => {
            const workflows = new Map(state.workflows);
            const workflow = workflows.get(conversationId);
            if (workflow) {
              workflows.set(conversationId, {
                ...workflow,
                steps: {
                  ...workflow.steps,
                  audio_generation: {
                    ...workflow.steps.audio_generation,
                    ...updates,
                  },
                },
                updatedAt: new Date(),
              });
            }
            return { workflows };
          }, false, 'updateAudioGenerationStatus');
        },

        // Update video generation status
        updateVideoGenerationStatus: (
          conversationId: string,
          updates: Partial<VideoGenerationStatus>
        ) => {
          set((state) => {
            const workflows = new Map(state.workflows);
            const workflow = workflows.get(conversationId);
            if (workflow) {
              workflows.set(conversationId, {
                ...workflow,
                steps: {
                  ...workflow.steps,
                  video_generation: {
                    ...workflow.steps.video_generation,
                    ...updates,
                  },
                },
                updatedAt: new Date(),
              });
            }
            return { workflows };
          }, false, 'updateVideoGenerationStatus');
        },

        // Progress to next step
        progressToNextStep: (conversationId: string) => {
          set((state) => {
            const workflows = new Map(state.workflows);
            const workflow = workflows.get(conversationId);
            if (workflow) {
              const stepOrder: WorkflowStepId[] = [
                'template_selection',
                'planning',
                'slide_generation',
                'audio_generation',
                'video_generation',
              ];
              const currentIndex = stepOrder.indexOf(workflow.currentStep);
              if (currentIndex < stepOrder.length - 1) {
                const nextStep = stepOrder[currentIndex + 1];
                workflows.set(conversationId, {
                  ...workflow,
                  currentStep: nextStep,
                  updatedAt: new Date(),
                });
              }
            }
            return { workflows };
          }, false, 'progressToNextStep');
        },

        // Handle workflow event from message
        handleWorkflowEvent: (conversationId: string, event: WorkflowEvent) => {
          const { type, data } = event;

          switch (type) {
            case 'workflow_initialized':
              get().initWorkflow(conversationId);
              get().setWorkflowEnabled(conversationId, true);
              break;

            case 'planning_started':
              get().updatePlanningStatus(conversationId, {
                status: 'in_progress',
                progress: 0,
                startedAt: new Date(),
                isExpanded: true,
              });
              get().progressToNextStep(conversationId);
              break;

            case 'planning_progress':
              get().updatePlanningStatus(conversationId, {
                progress: data?.progress || 0,
                subTasks: data?.subTasks,
              });
              break;

            case 'plan_generated':
              get().updatePlanningStatus(conversationId, {
                status: 'completed',
                progress: 100,
                completedAt: new Date(),
                canProceed: true,
                planId: data?.planId,
                planUrl: data?.planUrl,
                sceneCount: data?.sceneCount,
              });
              // Enable slide generation step
              get().updateSlideGenerationStatus(conversationId, {
                canProceed: true,
                totalSlides: data?.sceneCount || 0,
              });
              // Enable audio generation step (planning creates voiceover-script.txt)
              get().updateAudioGenerationStatus(conversationId, {
                canProceed: true,
              });
              break;

            case 'planning_error':
              get().updatePlanningStatus(conversationId, {
                status: 'error',
                error: data?.error || 'Planning failed',
              });
              break;

            case 'slide_generation_started':
              get().updateSlideGenerationStatus(conversationId, {
                status: 'in_progress',
                progress: 0,
                startedAt: new Date(),
                isExpanded: true,
                totalSlides: data?.totalSlides,
              });
              get().progressToNextStep(conversationId);
              break;

            case 'slide_progress':
              get().updateSlideGenerationStatus(conversationId, {
                progress: data?.progress || 0,
                currentSlide: data?.currentSlide,
                slides: data?.slides,
                completedSlides: data?.completedSlides,
              });
              break;

            case 'slide_completed':
              const workflow = get().getWorkflowState(conversationId);
              if (workflow) {
                const slideGenStatus = workflow.steps.slide_generation;
                const updatedSlides = [...(slideGenStatus.slides || [])];
                const slideIndex = updatedSlides.findIndex(
                  (s) => s.slideNumber === data?.slideNumber
                );
                if (slideIndex >= 0) {
                  updatedSlides[slideIndex] = {
                    ...updatedSlides[slideIndex],
                    status: 'complete',
                    progress: 100,
                    completedAt: new Date(),
                    ...data,
                  };
                } else {
                  updatedSlides.push({
                    id: data?.slideId || `slide-${String(data?.slideNumber).padStart(3, '0')}`,
                    slideNumber: data?.slideNumber,
                    status: 'complete',
                    progress: 100,
                    completedAt: new Date(),
                    ...data,
                  });
                }
                get().updateSlideGenerationStatus(conversationId, {
                  slides: updatedSlides,
                  completedSlides: updatedSlides.filter((s) => s.status === 'complete').length,
                });
              }
              break;

            case 'slide_generation_completed':
              get().updateSlideGenerationStatus(conversationId, {
                status: 'completed',
                progress: 100,
                completedAt: new Date(),
              });
              // Audio generation is already enabled after planning, no need to enable here
              break;

            case 'slide_generation_error':
              get().updateSlideGenerationStatus(conversationId, {
                status: 'error',
                error: data?.error || 'Slide generation failed',
              });
              break;

            case 'audio_generation_started':
              get().updateAudioGenerationStatus(conversationId, {
                status: 'in_progress',
                progress: 0,
                startedAt: new Date(),
                isExpanded: true,
              });
              break;

            case 'audio_generation_progress':
              get().updateAudioGenerationStatus(conversationId, {
                progress: data?.progress || 0,
              });
              break;

            case 'audio_generation_completed':
              get().updateAudioGenerationStatus(conversationId, {
                status: 'completed',
                progress: 100,
                completedAt: new Date(),
                audioUrl: data?.audioUrl,
                duration: data?.duration,
                voiceId: data?.voiceId,
                voiceName: data?.voiceName,
                languageCode: data?.languageCode,
                scriptLength: data?.scriptLength,
              });
              // Enable video generation step after audio is complete
              get().updateVideoGenerationStatus(conversationId, {
                canProceed: true,
              });
              break;

            case 'audio_generation_error':
              get().updateAudioGenerationStatus(conversationId, {
                status: 'error',
                error: data?.error || 'Audio generation failed',
              });
              break;

            case 'video_generation_started':
              get().updateVideoGenerationStatus(conversationId, {
                status: 'in_progress',
                progress: 0,
                startedAt: new Date(),
                isExpanded: true,
              });
              break;

            case 'video_generation_progress':
              get().updateVideoGenerationStatus(conversationId, {
                progress: data?.progress || 0,
                currentSubstep: data?.currentSubstep,
              });
              break;

            case 'video_generation_completed':
              get().updateVideoGenerationStatus(conversationId, {
                status: 'completed',
                progress: 100,
                completedAt: new Date(),
                manifestPath: data?.manifestPath,
                totalDuration: data?.totalDuration,
                slideCount: data?.slideCount,
              });
              break;

            case 'video_generation_error':
              get().updateVideoGenerationStatus(conversationId, {
                status: 'error',
                error: data?.error || 'Video generation failed',
              });
              break;

            case 'workflow_completed':
              // Mark all steps as completed
              get().updatePlanningStatus(conversationId, { status: 'completed' });
              get().updateSlideGenerationStatus(conversationId, { status: 'completed' });
              get().updateAudioGenerationStatus(conversationId, { status: 'completed' });
              get().updateVideoGenerationStatus(conversationId, { status: 'completed' });
              break;

            case 'workflow_error':
              const currentWorkflow = get().getWorkflowState(conversationId);
              if (currentWorkflow) {
                get().updateStepStatus(conversationId, currentWorkflow.currentStep, {
                  status: 'error',
                  error: data?.error || 'Workflow error occurred',
                });
              }
              break;
          }
        },

        // Toggle step expansion
        toggleStepExpanded: (conversationId: string, step: WorkflowStepId) => {
          const workflow = get().getWorkflowState(conversationId);
          if (workflow) {
            const currentStatus = workflow.steps[step];
            get().updateStepStatus(conversationId, step, {
              isExpanded: !currentStatus.isExpanded,
            });
          }
        },

        // Reset workflow to initial state
        resetWorkflow: (conversationId: string) => {
          set((state) => {
            const workflows = new Map(state.workflows);
            workflows.set(conversationId, createDefaultWorkflowState(conversationId));
            return { workflows };
          }, false, 'resetWorkflow');
        },

        // Clear workflow from store
        clearWorkflow: (conversationId: string) => {
          set((state) => {
            const workflows = new Map(state.workflows);
            workflows.delete(conversationId);
            return { workflows };
          }, false, 'clearWorkflow');
        },

        // Getters
        getWorkflowState: (conversationId: string) => {
          return get().workflows.get(conversationId) || null;
        },

        isWorkflowVisible: (conversationId: string) => {
          const workflow = get().workflows.get(conversationId);
          return workflow?.isVisible || false;
        },

        isWorkflowEnabled: (conversationId: string) => {
          const workflow = get().workflows.get(conversationId);
          return workflow?.isEnabled || false;
        },

        getCurrentStep: (conversationId: string) => {
          const workflow = get().workflows.get(conversationId);
          return workflow?.currentStep || null;
        },

        getStepStatus: (conversationId: string, step: WorkflowStepId) => {
          const workflow = get().workflows.get(conversationId);
          return workflow?.steps[step] || null;
        },
      }),
      {
        name: 'workflow-store',
        // Custom serializer to handle Map and Date objects
        storage: {
          getItem: (name) => {
            const str = localStorage.getItem(name);
            if (!str) return null;
            const { state } = JSON.parse(str);

            // Convert workflow entries and restore Date objects
            const workflowEntries = Object.entries(state.workflows || {}).map(([key, workflow]: [string, any]): [string, WorkflowState] => {
              return [
                key,
                {
                  ...workflow,
                  createdAt: workflow.createdAt ? new Date(workflow.createdAt) : undefined,
                  updatedAt: workflow.updatedAt ? new Date(workflow.updatedAt) : undefined,
                  steps: {
                    // Restore template_selection or use default if missing (migration)
                    template_selection: workflow.steps?.template_selection
                      ? {
                          ...workflow.steps.template_selection,
                          startedAt: workflow.steps.template_selection.startedAt
                            ? new Date(workflow.steps.template_selection.startedAt)
                            : undefined,
                          completedAt: workflow.steps.template_selection.completedAt
                            ? new Date(workflow.steps.template_selection.completedAt)
                            : undefined,
                        }
                      : createDefaultTemplateSelectionStatus(),
                    planning: {
                      ...workflow.steps.planning,
                      startedAt: workflow.steps.planning.startedAt
                        ? new Date(workflow.steps.planning.startedAt)
                        : undefined,
                      completedAt: workflow.steps.planning.completedAt
                        ? new Date(workflow.steps.planning.completedAt)
                        : undefined,
                    },
                    slide_generation: {
                      ...workflow.steps.slide_generation,
                      startedAt: workflow.steps.slide_generation.startedAt
                        ? new Date(workflow.steps.slide_generation.startedAt)
                        : undefined,
                      completedAt: workflow.steps.slide_generation.completedAt
                        ? new Date(workflow.steps.slide_generation.completedAt)
                        : undefined,
                      slides: workflow.steps.slide_generation.slides?.map((slide: any) => ({
                        ...slide,
                        completedAt: slide.completedAt ? new Date(slide.completedAt) : undefined,
                      })),
                    },
                    audio_generation: workflow.steps?.audio_generation
                      ? {
                          ...workflow.steps.audio_generation,
                          startedAt: workflow.steps.audio_generation.startedAt
                            ? new Date(workflow.steps.audio_generation.startedAt)
                            : undefined,
                          completedAt: workflow.steps.audio_generation.completedAt
                            ? new Date(workflow.steps.audio_generation.completedAt)
                            : undefined,
                          createdAt: workflow.steps.audio_generation.createdAt
                            ? new Date(workflow.steps.audio_generation.createdAt)
                            : undefined,
                        }
                      : createDefaultAudioGenerationStatus(),
                    video_generation: workflow.steps?.video_generation
                      ? {
                          ...workflow.steps.video_generation,
                          startedAt: workflow.steps.video_generation.startedAt
                            ? new Date(workflow.steps.video_generation.startedAt)
                            : undefined,
                          completedAt: workflow.steps.video_generation.completedAt
                            ? new Date(workflow.steps.video_generation.completedAt)
                            : undefined,
                        }
                      : createDefaultVideoGenerationStatus(),
                  },
                } as WorkflowState,
              ];
            });

            return {
              state: {
                ...state,
                workflows: new Map<string, WorkflowState>(workflowEntries),
              },
            };
          },
          setItem: (name, value) => {
            const workflows = Object.fromEntries(value.state.workflows);
            localStorage.setItem(
              name,
              JSON.stringify({
                state: {
                  ...value.state,
                  workflows,
                },
              })
            );
          },
          removeItem: (name) => localStorage.removeItem(name),
        },
      }
    ),
    {
      name: 'workflow-store',
    }
  )
);

// Expose to window for console access during development
if (typeof window !== 'undefined') {
  (window as any).useWorkflowStore = useWorkflowStore;
  (window as any).workflowStore = useWorkflowStore.getState();
}
